import React, { useEffect } from 'react';

import { setFocus } from '@noriginmedia/norigin-spatial-navigation';

import Button from '@/components/Button';
import CustomTypography from '@/components/Typography/CustomTypography';

import { CraveError } from '@/types';

import { useAccessibility } from '../Accessibility/AccessibilityContext';
import { useGlobalConfig } from '../GlobalContext';

type Props = {
  error: CraveError;
  buttonLabel?: string;
  onKeyPress?: () => void;
};
export const ErrorOverlay: React.FC<Props> = (props: Props): JSX.Element => {
  const { error, buttonLabel, onKeyPress } = props;
  const { localization } = useGlobalConfig();
  const { announceToScreenReader } = useAccessibility();

  useEffect(() => {
    if (!error) return;

    const buttonCopy = localization?.['generic']['button'] || '';
    const ariaLabel = `${error.title}, ${error.errorCode}, ${buttonLabel ? buttonLabel + buttonCopy : ''}`;
    announceToScreenReader(ariaLabel, 'assertive');
    let focusTimeout: number | undefined;

    // Delay focus assignment until focusable children are registered.
    if (buttonLabel && onKeyPress) {
      focusTimeout = window.setTimeout(() => {
        setFocus('error-button');
      }, 0);
    }

    return () => {
      if (focusTimeout !== undefined) {
        window.clearTimeout(focusTimeout);
      }
      announceToScreenReader('', 'assertive');
    };
  }, [announceToScreenReader, buttonLabel, error, localization, onKeyPress]);

  const errorCopy = `${error?.message} - ${error.errorCode}`;

  return (
    <div className="flex flex-col w-screen h-screen bg-black justify-center items-center">
      <div className="w-[60rem] text-center items-center flex flex-col justify-center">
        <div>
          <CustomTypography
            variant="10ftTitle1"
            styleClasses="mb-800 line-clamp-2 !text-componentEmphasized"
          >
            {error.title}
          </CustomTypography>
          <CustomTypography
            variant="10ftBody"
            styleClasses="line-clamp-2 !text-componentSubtle"
          >
            {errorCopy}
          </CustomTypography>
        </div>
        {buttonLabel && onKeyPress && (
          <div className="text-black">
            <Button
              focusKey="error-button"
              variant="focused"
              size="small"
              onClick={onKeyPress}
              className="flex group mt-16 bg-white text-black"
            >
              {buttonLabel}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
